import { CommonModule } from '@angular/common';
import { Component, inject, input, OnChanges, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { CompanyPermissionGroupMapping, User } from '@src/app/shared/models';
import { BroadcastChannelService } from '@src/app/shared/services/broadcast-channel/broadcast-channel.service';
import * as fromStore from '@src/app/store/conventional';
import { filter, map } from 'rxjs';
import { IconComponent, icons } from '../../icon';
import { PopupComponent } from '../popup.component';


function sortCompanyListAlphabetically(companies: CompanyPermissionGroupMapping[]): CompanyPermissionGroupMapping[] {
  return companies.sort((a, b) => {
    const aName = a.companyName.toLowerCase();
    const bName = b.companyName.toLowerCase();

    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  });
}

@Component({
  selector: 'cs-popup-company',
  imports: [
    CommonModule,
    ButtonModule,
    PopupComponent,
    LoaderModule,
    IconComponent
  ],
  template: `
    <cs-popup role="dialog" aria-label="company popup button">
      <button kendoButton csPopupAnchorContent [svgIcon]="chevronDownIcon"
        class="max-w-[185px] flex-row-reverse border-none bg-transparent px-0 text-[14px] text-white hover:bg-transparent md:max-w-[299px]">
        <div class="flex min-w-0 flex-col overflow-hidden">
          <span class="truncate">{{activeCompany?.companyName}}</span>
          @if(activeCompany && !activeCompany.active) {
            <span class="text-left text-[12px] text-gray-500">Deactivated</span>
          }
        </div>
      </button>
      <ul csPopupPanelContent>
        @for(company of companyList; track company.companyId) {
        <li [attr.data-testid]="company.companyId + '-list'" class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-[#F3F4F6]" (click)="setActiveCompany(company)">
          <div class="flex flex-col gap-y-1">
            <span class="line-clamp-2 max-w-[180px] text-[14px]">{{company.companyName}}</span>
            <span class="text-[12px] text-gray-500">{{company.companyId}}</span>
            @if(company && !company.active) {
              <span class="text-[12px] text-gray-500">Deactivated</span>
            }
          </div>
          @if(activeCompany?.companyId === company.companyId && !(changingActiveCompany() || refreshingToken())) {
            <cs-icon role="listitem" aria-label="active-company" name="check" size="large"></cs-icon>
          }
          @if(
            (changingActiveCompany() || refreshingToken()) && 
            company.companyId === newActiveCompanyId()) {
              <cs-icon data-testid="changing-active-company" class="animate-spin" name="loadingSpinner" size="large"></cs-icon>
          }
        </li>
        }
      </ul>
    </cs-popup>
  `,
  styles: `
    :host ::ng-deep .k-button-text {
      @apply overflow-hidden text-ellipsis;
    }
  `
})
export class PopupCompanyTemplateComponent implements OnInit, OnChanges {
  private store = inject(Store<fromStore.State>);
  private readonly dialogService = inject(DialogService);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly router = inject(Router);

  public user = input<User>();

  public chevronDownIcon = icons['chevronDown'];

  public activeCompany: CompanyPermissionGroupMapping | undefined = undefined;
  public companyList: CompanyPermissionGroupMapping[] = [];
  public changingActiveCompany = toSignal(this.store.select(
    fromStore.selectChangingActiveCompany),
    { initialValue: false }
  );
  public refreshingToken = toSignal(this.store.select(
    fromStore.selectRefreshingToken),
    { initialValue: false }
  );
  public newActiveCompanyId = signal<string | undefined>(undefined);

  private isUrlInShipmentWizzard = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => event.url.includes('/shipments/create'))
    ),
    { initialValue: false }
  )

  constructor() { }

  public ngOnInit(): void {
    this.broadcastChannelService.subscribeToMessage('company-changed').subscribe(({ data }) => {
      if (data.currentCompany?.companyId === data.newCompany.companyId) {
        return;
      }

      this.showCompanyChangeDialog({
        newCompany: data.newCompany
      });
    });
  }

  public ngOnChanges(): void {
    this.activeCompany = this.user()?.associatedCompanies?.find(c => c.companyId === this.user()?.activeCompanyId);
    this.companyList = sortCompanyListAlphabetically([...(this.user()?.associatedCompanies ?? [])]);
  }

  public setActiveCompany(company: CompanyPermissionGroupMapping): void {
    if (company.companyId === this.activeCompany?.companyId) {
      return;
    }
    this.newActiveCompanyId.set(company.companyId);
    this.broadcastChannelService.publish({
      type: 'company-changed',
      data: {
        currentCompany: this.activeCompany ?? undefined,
        newCompany: company
      }
    });
    this.showCompanyChangeDialog({
      newCompany: company,
      dialogContent: `Your active session has just switched from ${this.activeCompany?.companyName} to ${company.companyName}. Any unsaved progress on this shipment will be discarded.`,
      timeoutMs: 0
    });
  };

  private showCompanyChangeDialog({
    newCompany,
    dialogTitle = "Session Conflict",
    dialogContent = "You've changed your active organization in another tab. You will be redirected to the shipment list.",
    timeoutMs = 4000
  }: {
    newCompany: CompanyPermissionGroupMapping;
    dialogTitle?: string;
    dialogContent?: string;
    timeoutMs?: number;
  }): void {
    if (this.isUrlInShipmentWizzard()) {
      const dialogRef = this.dialogService.open({
        title: dialogTitle,
        content: dialogContent,
        cssClass: 'profile-change-dialog ',
      });

      dialogRef.result.subscribe((result) => {
        if (result instanceof DialogCloseResult) {
          this.store.dispatch(fromStore.setActiveCompany({
            companyPermissionGroupMapping: newCompany
          }));
        }
      });
    }

    setTimeout(() => {
      this.store.dispatch(fromStore.setActiveCompany({
        companyPermissionGroupMapping: newCompany
      }));
    }, timeoutMs);
  }
}
